import { Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { DataReportService } from '@app/_components/data-report/data-report.service';

/**
 * Tile model for the top-of-page Google sync status dashboard.
 *
 * Each tile corresponds to one Google ARI feed and shows the number of
 * distinct properties that synced PASS / WARN / FAIL in the lookback
 * window. Numbers come from /dataReports/fetch-google-sync-dashboard.
 */
interface SyncTile {
  testName: string; // e.g. 'GoogleLosRateSync'
  label: string;    // e.g. 'LOS Rate'
  pass: number;
  warn: number;
  fail: number;
}

@Component({
    selector: 'app-google',
    templateUrl: './google.component.html',
    styleUrls: ['./google.component.css'],
    standalone: false
})
export class GoogleComponent implements OnInit {
  tabIndex = 0;

  // Lookback for the dashboard tiles. Bound to a small dropdown so the user
  // can flip between 1 / 3 / 7 days without leaving the page.
  daysBack: number = 3;
  daysBackOptions = [
    { label: 'Last 1 day',  value: 1 },
    { label: 'Last 3 days', value: 3 },
    { label: 'Last 7 days', value: 7 }
  ];

  // Display order matches the tab order. Pre-seeded with zeros so the
  // template can render skeleton tiles before the API responds.
  dashboardTiles: SyncTile[] = [
    { testName: 'GoogleUnitAvailabilitySync', label: 'Unit Availability', pass: 0, warn: 0, fail: 0 },
    { testName: 'GoogleInvCountSync',         label: 'Inv Count',         pass: 0, warn: 0, fail: 0 },
    { testName: 'GoogleLosRateSync',          label: 'LOS Rate',          pass: 0, warn: 0, fail: 0 },
    { testName: 'GoogleTaxFeeInfoSync',       label: 'Tax / Fee',         pass: 0, warn: 0, fail: 0 },
    { testName: 'GoogleVrFeedUpload',         label: 'VR Feed',           pass: 0, warn: 0, fail: 0 }
  ];

  dashboardLoading = false;

  // External link the user asked for — opens Google Hotel Center's price
  // accuracy dashboard in a new tab. Account id is hard-coded per request.
  hotelCenterUrl = 'https://hotelcenter.google.com/prices/accuracy?a=2034988975';

  constructor(
    private dataReportService: DataReportService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.loadDashboard();
  }

  /**
   * Fetch the aggregated counts for all 5 feeds in one call. The endpoint
   * runs a single GROUP BY query so payload is tiny (~25 entries) regardless
   * of how many sync rows exist in the underlying table.
   */
  loadDashboard(): void {
    this.dashboardLoading = true;
    this.dataReportService.fetchGoogleSyncDashboard(this.daysBack).subscribe(
      (res: any) => {
        const summary: { [k: string]: { [k: string]: number } } = res?.data?.summary || {};
        this.dashboardTiles = this.dashboardTiles.map(tile => {
          const counts = summary[tile.testName] || {};
          return {
            ...tile,
            pass: Number(counts['PASS'] || 0),
            warn: Number(counts['WARN'] || 0),
            fail: Number(counts['FAIL'] || 0)
          };
        });
        this.dashboardLoading = false;
      },
      (err: any) => {
        this.dashboardLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Dashboard',
          detail: err?.message || 'Failed to load Google sync dashboard'
        });
      }
    );
  }

  /** Re-fetch when the daysBack dropdown changes. */
  onDaysBackChange(): void {
    this.loadDashboard();
  }
}
